import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ShieldOff, Play, Loader2, Copy, Check, RotateCcw, ExternalLink, Clock, Router, FileCode2, ListChecks } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const card = "p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-4";
const input = "w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white";
const rp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;

const mikrotikScript = (host, url, profile) => `# === 1. Profil & pool khusus pelanggan isolir ===
/ip pool add name=pool-isolir ranges=10.255.0.2-10.255.255.254
/ppp profile add name=${profile} local-address=10.255.0.1 remote-address=pool-isolir \\
    address-list=isolir dns-server=10.255.0.1 rate-limit=256k/256k comment="Profil isolir billing"
/ip dns set allow-remote-requests=yes

# === 2. Alamat yang tetap boleh dibuka pelanggan isolir ===
/ip firewall address-list add list=isolir-allow address=${host} comment="Aplikasi billing"

# === 3. Web proxy untuk menampilkan halaman isolir ===
/ip proxy set enabled=yes port=8099
/ip proxy access add dst-host=${host} action=allow comment="Billing"
/ip proxy access add dst-host=*wa.me* action=allow comment="WhatsApp CS"
# RouterOS v7:
/ip proxy access add action=redirect action-data="${url}" comment="Halaman isolir"
# RouterOS v6 (pakai baris ini sebagai ganti baris v7 di atas):
# /ip proxy access add action=deny redirect-to="${url.replace(/^https?:\/\//, "")}" comment="Halaman isolir"

# === 4. Arahkan akses web (HTTP) pelanggan isolir ke web proxy ===
/ip firewall nat add chain=dstnat src-address-list=isolir protocol=tcp dst-port=80 \\
    action=redirect to-ports=8099 comment="Isolir redirect"

# === 5. Blokir internet lain untuk pelanggan isolir ===
/ip firewall filter add chain=input src-address-list=isolir protocol=tcp dst-port=8099 action=accept comment="Isolir proxy"
/ip firewall filter add chain=forward src-address-list=isolir dst-address-list=isolir-allow action=accept comment="Isolir allow"
/ip firewall filter add chain=forward src-address-list=isolir action=drop comment="Isolir block"`;

const Guide = ({ profile }) => {
  const [copied, setCopied] = useState(false);
  const host = window.location.host;
  const url = `${window.location.origin}/api/isolir-page`;
  const script = mikrotikScript(host, url, profile || "ISOLIR");
  const copy = () => { navigator.clipboard.writeText(script); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const steps = [
    "Buka Winbox → New Terminal di router MikroTik yang dipakai pelanggan PPPoE.",
    "Salin script di bawah lalu tempel ke terminal (sudah berisi alamat aplikasi Anda & nama profil isolir).",
    "Buka IP → Firewall → Filter Rules, geser 3 rule berkomentar \"Isolir ...\" ke urutan paling atas agar tidak kalah oleh rule lain.",
    `Pastikan router sudah terdaftar di Jaringan → Mikrotik dan pelanggan memiliki username PPPoE + router yang terhubung.`,
    "Uji: klik \"Jalankan Isolir Sekarang\" untuk pelanggan uji, lalu dari HP pelanggan buka situs http (mis. http://neverssl.com) — halaman isolir akan muncul.",
  ];
  return (
    <div className={card} data-testid="isolir-guide">
      <h3 className="text-sm font-bold text-white flex items-center gap-2"><Router className="w-4 h-4 text-amber-400" />Panduan Integrasi MikroTik</h3>
      <ol className="space-y-1.5 text-xs text-slate-300 list-decimal pl-4">
        {steps.map((s) => <li key={s}>{s}</li>)}
      </ol>
      <div className="relative">
        <button type="button" onClick={copy} data-testid="isolir-script-copy" className="absolute top-2 right-2 px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-[11px] font-bold text-slate-200 flex items-center gap-1">
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}{copied ? "Tersalin" : "Salin Script"}
        </button>
        <pre className="p-3 pt-10 bg-black/60 border border-slate-800 rounded-xl text-[10.5px] leading-relaxed text-emerald-300 font-mono overflow-x-auto whitespace-pre" data-testid="isolir-script">{script}</pre>
      </div>
      <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-200 space-y-1">
        <p><b>Catatan:</b> situs HTTPS (Google, YouTube, dll) tidak bisa dialihkan — pelanggan cukup melihat "tidak bisa terhubung". Halaman isolir muncul saat membuka situs http biasa.</p>
        <p>Saat tagihan lunas, aplikasi otomatis mengembalikan profil PPPoE asli pelanggan dan memutus sesi agar langsung tersambung normal.</p>
      </div>
    </div>
  );
};

const PageEditor = ({ form, setField }) => {
  const [preview, setPreview] = useState("");
  const [vars, setVars] = useState([]);
  const [defHtml, setDefHtml] = useState("");
  const html = form.isolir_page_html || "";

  useEffect(() => {
    const t = setTimeout(() => {
      axios.post(`${BACKEND_URL}/api/isolir/preview`, { html }, { withCredentials: true })
        .then((r) => { setPreview(r.data.html); setVars(r.data.variables || []); setDefHtml(r.data.default_html || ""); })
        .catch(() => setPreview("<p style='font-family:sans-serif;padding:20px'>Gagal memuat pratinjau</p>"));
    }, 400);
    return () => clearTimeout(t);
  }, [html]);

  return (
    <div className={card} data-testid="isolir-page-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-bold text-white flex items-center gap-2"><FileCode2 className="w-4 h-4 text-cyan-400" />Halaman Isolir (HTML)</h3>
        <div className="flex gap-2">
          <button type="button" onClick={() => setField("isolir_page_html", "")} data-testid="isolir-html-reset" className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-[11px] font-bold text-slate-300 flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" />Pakai Default</button>
          <a href={`${BACKEND_URL}/api/isolir-page`} target="_blank" rel="noreferrer" data-testid="isolir-public-link" className="px-2.5 py-1.5 rounded-lg bg-cyan-600/20 border border-cyan-600/40 text-[11px] font-bold text-cyan-300 flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" />Buka Halaman Publik</a>
        </div>
      </div>
      <p className="text-[11px] text-slate-400">Variabel otomatis dari Profil Perusahaan: {vars.map((v) => <code key={v} className="mx-0.5 px-1 rounded bg-slate-800 text-cyan-300">{`{${v}}`}</code>)}</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <textarea
          value={html || defHtml}
          onChange={(e) => setField("isolir_page_html", e.target.value === defHtml ? "" : e.target.value)}
          spellCheck={false}
          data-testid="isolir-html-input"
          className={`${input} font-mono text-[11px] leading-relaxed h-[460px] resize-y`}
        />
        <div>
          <p className="text-[11px] font-semibold text-slate-400 mb-1">Pratinjau (tampilan HP)</p>
          <div className="mx-auto w-full max-w-[340px] h-[440px] rounded-[28px] border-4 border-slate-700 overflow-hidden bg-white">
            <iframe title="Pratinjau halaman isolir" srcDoc={preview} sandbox="" className="w-full h-full" data-testid="isolir-preview-frame" />
          </div>
        </div>
      </div>
      <p className="text-[10px] text-slate-500">Jangan lupa klik <b>Simpan Pengaturan</b> agar perubahan HTML dipakai di halaman publik.</p>
    </div>
  );
};

const Kandidat = () => {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    axios.get(`${BACKEND_URL}/api/isolir/kandidat`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
    axios.get(`${BACKEND_URL}/api/isolir/logs`, { withCredentials: true }).then((r) => setLogs(r.data || [])).catch(() => setLogs([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    const n = (data?.items || []).filter((i) => !i.ditunda).length;
    if (!window.confirm(`Isolir ${n} pelanggan sekarang? Profil PPPoE mereka akan dipindah ke profil isolir.`)) return;
    setRunning(true); setMsg("");
    try {
      const r = await axios.post(`${BACKEND_URL}/api/isolir/run`, {}, { withCredentials: true });
      setMsg(`Selesai: ${r.data.diisolir} diisolir, ${r.data.gagal} gagal, ${r.data.ditunda} ditunda.`);
      load();
    } catch (e) { setMsg(e.response?.data?.detail || "Gagal menjalankan isolir"); }
    finally { setRunning(false); }
  };

  const items = data?.items || [];
  return (
    <div className={card} data-testid="isolir-kandidat">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-bold text-white flex items-center gap-2"><ListChecks className="w-4 h-4 text-rose-400" />Akan Diisolir ({items.filter((i) => !i.ditunda).length})</h3>
        <button type="button" onClick={run} disabled={running || !items.some((i) => !i.ditunda)} data-testid="isolir-run-btn" className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:opacity-40 text-white text-[11px] font-bold flex items-center gap-1.5">
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}Jalankan Isolir Sekarang
        </button>
      </div>
      {msg && <p className="text-[11px] text-cyan-300" data-testid="isolir-run-result">{msg}</p>}
      {!data ? <p className="text-xs text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />Memuat...</p> : items.length === 0 ? (
        <p className="text-xs text-slate-500 italic" data-testid="isolir-kandidat-empty">Tidak ada pelanggan aktif yang melewati batas isolir hari ini.</p>
      ) : (
        <div className="max-h-64 overflow-y-auto space-y-1.5">
          {items.map((i) => (
            <div key={i.pelanggan_id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-slate-950 border border-slate-800 text-[11px]" data-testid={`isolir-kandidat-${i.pelanggan_id}`}>
              <div className="min-w-0">
                <p className="font-bold text-white truncate">{i.nama} <span className="font-mono text-slate-500">{i.no_layanan}</span></p>
                <p className="text-slate-400">Jatuh tempo {i.jatuh_tempo} · {i.jumlah_tagihan} tagihan · {rp(i.total)}{!i.terhubung_router && " · belum terhubung router"}</p>
              </div>
              {i.ditunda ? <span className="px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 font-bold whitespace-nowrap">Ditunda s/d {i.tunda_isolir_sampai}</span>
                : <span className="px-2 py-0.5 rounded bg-rose-500/15 border border-rose-500/30 text-rose-300 font-bold">Isolir</span>}
            </div>
          ))}
        </div>
      )}
      {logs.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-slate-400 mb-1">Riwayat terakhir</p>
          <div className="max-h-40 overflow-y-auto space-y-1" data-testid="isolir-logs">
            {logs.map((l, idx) => (
              <p key={idx} className={`text-[10.5px] ${l.ok ? "text-slate-300" : "text-rose-300"}`}>
                {new Date(l.created_at).toLocaleString("id-ID")} · <b>{l.aksi === "buka" ? "Buka" : "Isolir"}</b> {l.nama} ({l.sumber}) — {l.pesan}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const IsolirSettingsTab = ({ form, setField }) => (
  <div className="space-y-5" data-testid="tab-isolir-content">
    <div className={card}>
      <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldOff className="w-4 h-4 text-rose-400" />Isolir Otomatis</h3>
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={!!form.isolir_otomatis_aktif} onChange={(e) => setField("isolir_otomatis_aktif", e.target.checked)} className="w-4 h-4 accent-rose-500" data-testid="auto-isolir-toggle" />
        <span className="text-xs text-slate-200">Aktifkan isolir otomatis saat tagihan lewat jatuh tempo</span>
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-slate-300 mb-1">Isolir setelah lewat jatuh tempo (hari)</label>
          <input type="number" min={0} max={60} value={form.grace_period_days ?? 5} onChange={(e) => setField("grace_period_days", Math.max(0, parseInt(e.target.value || "0", 10)))} className={`${input} font-mono`} data-testid="isolir-grace-days" />
          <p className="text-[10px] text-slate-500 mt-1">Contoh: jatuh tempo tgl 10, isi 5 → diisolir pada tgl 16 pukul 01:00 WIB.</p>
        </div>
        <div>
          <label className="block text-xs text-slate-300 mb-1">Nama profil PPPoE isolir di MikroTik</label>
          <input type="text" value={form.isolir_profile || "ISOLIR"} onChange={(e) => setField("isolir_profile", e.target.value.trim())} className={`${input} font-mono`} data-testid="isolir-profile-input" />
          <p className="text-[10px] text-slate-500 mt-1">Profil asli pelanggan disimpan & dikembalikan otomatis saat lunas.</p>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-cyan-400" />Pengecekan berjalan otomatis setiap hari pukul 01:00 WIB. Pelanggan yang ditandai "Tunda Isolir" di Data Pelanggan dilewati.</p>
    </div>
    <Kandidat />
    <Guide profile={form.isolir_profile} />
    <PageEditor form={form} setField={setField} />
  </div>
);
